import React, { useState, useEffect } from "react";
import { Form, Image, Button, Icon, Divider, Checkbox } from "semantic-ui-react";
import { useFormik } from "formik";
import { Role, Company } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { isAdmin, isMaster } from "../../../../utils/checkPermission";
import { initialValues, validationSchema } from "./CompanyForm.form";
import "./CompanyForm.scss";
import { extractDomain } from "../../../../utils/formatEmail";
import { useLanguage } from "../../../../contexts";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import NoImagen from "../../../../utils/image-2935360_1280.png";
import { useNavigate } from "react-router-dom";

const companyController = new Company();
const roleController = new Role();

export function CompanyForm(props) {
  const { close, onReload, company } = props;
  const {
    accessToken,
    user: { role },
  } = useAuth();
  const [listRoles, setListRoles] = useState([]);
  const [logo, setLogo] = useState(null); // Estado para almacenar el logo

  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const navigate = useNavigate();

  useEffect(() => {
    roleController.getRoles(accessToken, true).then((response) => {
      setListRoles(response);
    });
  }, []);

  const formik = useFormik({
    initialValues: initialValues(company),
    validationSchema: validationSchema(company),
    validateOnChange: false,
    onSubmit: async (formValue, { setSubmitting }) => {
      try {
        if (!company) {
          const response = await companyController.createCompany(
            accessToken,
            formValue
          );
          if (response.code === 200) {
            toast.success(t("form_submitted_successfully"));
            if (response.integrationWarning) window.alert(response.integrationWarning);
          } else {
            toast.error(`${response.msg}`);
          }
        } else {
          const response2 = await companyController.updateCompany(
            accessToken,
            company._id,
            formValue
          );
          if (response2.code === 200) {
            toast.success(t("form_updated_successfully"));
            if (response2.integrationWarning) window.alert(response2.integrationWarning);
          } else {
            toast.error(`${response2.msg}`);
          }
        }
        setTimeout(() => {
          close();
          onReload();
        }, 1000);
      } catch (error) {
        toast.error(
          `${error.msg ? error.msg : t("server_error_try_again")}`
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Manejar el cambio de imagen del logo
  // const handleLogoChange = (e) => {
  //   const file = e.target.files[0];
  //   if (file) {
  //     setLogo(URL.createObjectURL(file)); // Crear una URL para la vista previa
  //     formik.setFieldValue("logo", file); // Guardar el archivo en Formik
  //   }
  // };

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        if (company && company.logo) {
          const response = await companyController.getFileApi(company.logo);
          setLogo(response); // Establecer la URL del logo
        }
      } catch (error) {
        console.error("Error al obtener el logo:", error);
      }
    };

    fetchLogo();
  }, [company]);

  const handleChangeEmail = (e, formik) => {
    formik.setFieldValue("email", e.target.value);
    formik.setFieldValue("domain", extractDomain(e.target.value));
  };

  function formatCuit(cuit) {
    cuit = cuit.replace(/\D/g, "");
    if (cuit.length <= 2) return cuit;
    if (cuit.length <= 10) return cuit.slice(0, 2) + "-" + cuit.slice(2);
    return (
      cuit.slice(0, 2) + "-" + cuit.slice(2, 10) + "-" + cuit.slice(10, 11)
    );
  }

  // Función para manejar el envío del formulario (incluyendo la imagen)
  const handleLogoChange = async (e) => {
    e.preventDefault();

    // Crear un FormData para enviar la imagen
    const formData = new FormData();
    formData.append("file", e.target.files[0]); // 'logo' es el nombre del campo en el backend
    setLogo(URL.createObjectURL(e.target.files[0])); // Crear una URL para la vista previa
    try {
      const response = await companyController.uploadFileApi(
        accessToken,
        formData
      );
      //console.log("Imagen cargada con éxito:", response);
      if (response.file) {
        formik.setFieldValue("logo", response.file.uniqueName); // Guardar el archivo en Formik
      }
      // Aquí puedes manejar la respuesta, como cerrar el modal y recargar los datos si es necesario
    } catch (error) {
      console.error("Error al cargar la imagen:", error);
    }
  };

  const handleRemoveFile = async (e) => {
    try {
      e.preventDefault();
      const response = await companyController.deleteFileApi(
        accessToken,
        formik.values.logo
      );
      if (response && response.status === 200) {
        formik.setFieldValue("logo", null);
      }
      //setMessage(response.message);
      setLogo("");
    } catch (error) {
      // setMessage('Error al elimianr el archivo');
    }
  };

  const goBack = () => {
    navigate(`/admin/companies`, {
      //state: { siteSelected: siteSelected },
    });
  };

  return (
    <Form className="company-form" onSubmit={formik.handleSubmit}>
      {/* Foto */}
      <div className="company-form__foto-wrapper">
        <div className="company-form__foto-container">
          <label htmlFor="logo-upload" className="company-form__foto-label">
            {logo ? (
              <Image src={logo} alt="Logo" circular size="small" />
            ) : (
              <div className="company-form__foto-placeholder">
                <span>{t("photo") || "Foto"}</span>
              </div>
            )}
          </label>
          {logo && (
            <Button
              className="company-form__foto-remove"
              icon="trash"
              circular
              size="mini"
              color="red"
              type="button"
              onClick={handleRemoveFile}
            />
          )}
        </div>
        <input
          type="file"
          id="logo-upload"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleLogoChange}
        />
      </div>

      <Form.Group widths="equal">
        <Form.Input
          label={<label>{t("company_name")} <span style={{ color: "#C0392B" }}>*</span></label>}
          name="name"
          onChange={formik.handleChange}
          value={formik.values.name}
          error={formik.errors.name}
        />
        <Form.Input
          label={<label>{t("cuit")} <span style={{ color: "#C0392B" }}>*</span></label>}
          name="cuit"
          onChange={formik.handleChange}
          value={formik.values.cuit ? formatCuit(formik.values.cuit) : ""}
          error={formik.errors.cuit}
          maxLength={13}
        />
      </Form.Group>

      <Form.Group widths="equal">
        <Form.Input
          label={<label>{t("phone")} <span style={{ color: "#C0392B" }}>*</span></label>}
          name="phone"
          onChange={formik.handleChange}
          value={formik.values.phone}
          error={formik.errors.phone}
        />
        <Form.Input
          label={<label>{t("address")} <span style={{ color: "#C0392B" }}>*</span></label>}
          name="address"
          onChange={formik.handleChange}
          value={formik.values.address}
          error={formik.errors.address}
        />
      </Form.Group>

      <Form.Group widths="equal">
        <Form.Input
          label={<label>{t("email")} <span style={{ color: "#C0392B" }}>*</span></label>}
          name="email"
          onChange={formik.handleChange}
          value={formik.values.email}
          error={formik.errors.email}
        />
      </Form.Group>

      {/* Productos contratados: solo los define el master. Al marcar
          Sustentable la empresa y sus sitios se replican allá (por CUIT). */}
      {isMaster(role) && (
        <div className="company-form__products">
          <span className="company-form__products-title">{t("contracted_products")}</span>
          <div className="company-form__products-grid">
            {[
              { key: "sh", name: "Sustemia SH", desc: t("product_sh_desc") },
              { key: "sustentable", name: "Sustemia Sustentable", desc: t("product_sustentable_desc") },
            ].map((product) => {
              const on = Boolean(formik.values.products[product.key]);
              const toggle = () => formik.setFieldValue(`products.${product.key}`, !on);
              return (
                <div
                  key={product.key}
                  role="switch"
                  aria-checked={on}
                  tabIndex={0}
                  className={`company-form__product${on ? " company-form__product--on" : ""}`}
                  onClick={toggle}
                  onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Enter") { e.preventDefault(); toggle(); }
                  }}
                >
                  <div>
                    <div className="company-form__product-name">{product.name}</div>
                    <div className="company-form__product-desc">{product.desc}</div>
                  </div>
                  <Checkbox toggle readOnly checked={on} tabIndex={-1} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="company-form__actions">
        <button type="submit" disabled={formik.isSubmitting} className="company-form__pill company-form__pill--primary">
          {formik.isSubmitting ? "…" : (company ? t("update") : t("save"))}
        </button>
        <button type="button" onClick={() => { close ? close() : goBack(); }} className="company-form__pill company-form__pill--cancel">
          {t("cancel")}
        </button>
      </div>

      <ToastContainer
        position="top-center"
        autoClose={800}
        hideProgressBar
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </Form>
  );
}
